import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { DashboardTypography } from "../../../components/Dashboard/DashboardTypography";
import { InsightsIcon } from "../../../components/Dashboard/InsightsIcon";
import { useRevealWage } from "../../../hooks/useRevealWage";
import type { MultiFacilityKpiSummaryResponse } from "../../../utils/models";
import { STATISTICS_CARD_HEIGHT } from "../visualizers/Statistics";
import { MultiFacilityChartStateBox } from "./MultiFacilityChartStateBox";
import { formatMultiFacilityKpi } from "./utils";

/** Narrowest a KPI card may get before the row wraps (keeps values readable on tablet/mobile). */
const KPI_CARD_MIN_WIDTH = "120px";

type KpiCard = {
	labelKey: string;
	testId: string;
	value: string;
	/** Wage-gated cards are OMITTED entirely while the user's wage reveal toggle is off (mirrors single mode). */
	wageGated: boolean;
};

type Props = {
	/** KPI summary for the whole portfolio; undefined while loading (every value renders "-"). */
	data?: MultiFacilityKpiSummaryResponse;
	/** Query failure: the whole row renders the shared chart-error treatment instead of dash-valued cards. */
	isError: boolean;
};

/**
 * Portfolio KPI header row: seven stat cards over the multi-facility charts. Cost KPIs (`avgPpd`,
 * `totalCost`) may arrive blanked by the backend (any facility hides wage) and render as "-" —
 * the hour-based KPIs still show real numbers. The wage reveal toggle omits the two cost cards
 * entirely, exactly like the single-mode Statistics row. A failed query collapses the row into a
 * single error card so it can't be mistaken for still-loading dashes.
 */
export const MultiFacilityKpiRow: FC<Props> = ({ data, isError }) => {
	const { t } = useTranslation();
	const revealWage = useRevealWage();

	if (isError) {
		return (
			<Box data-testid="mc-multi-kpi-row" sx={{ width: "100%" }}>
				<CommonDashboardBox sx={{ width: "100%", height: STATISTICS_CARD_HEIGHT, display: "flex" }}>
					<MultiFacilityChartStateBox isError height="100%" />
				</CommonDashboardBox>
			</Box>
		);
	}

	const cards: KpiCard[] = [
		{ labelKey: "mc.multi.kpi.facilities", testId: "mc-multi-kpi-facilities", value: formatMultiFacilityKpi(data?.facilities, "integer"), wageGated: false },
		{ labelKey: "mc.multi.kpi.avg.ppd", testId: "mc-multi-kpi-avg-ppd", value: formatMultiFacilityKpi(data?.avgPpd, "dollars2"), wageGated: true },
		{ labelKey: "mc.multi.kpi.avg.hppd", testId: "mc-multi-kpi-avg-hppd", value: formatMultiFacilityKpi(data?.avgHppd, "decimal2"), wageGated: false },
		{ labelKey: "mc.multi.kpi.occupancy", testId: "mc-multi-kpi-occupancy", value: formatMultiFacilityKpi(data?.occupancy, "percent1"), wageGated: false },
		{
			labelKey: "mc.multi.kpi.agency.percent",
			testId: "mc-multi-kpi-agency-percent",
			value: formatMultiFacilityKpi(data?.agencyPercent, "percent1"),
			wageGated: false,
		},
		{ labelKey: "mc.multi.kpi.ot.percent", testId: "mc-multi-kpi-ot-percent", value: formatMultiFacilityKpi(data?.otPercent, "percent1"), wageGated: false },
		{ labelKey: "mc.multi.kpi.total.cost", testId: "mc-multi-kpi-total-cost", value: formatMultiFacilityKpi(data?.totalCost, "kDollars"), wageGated: true },
	];

	return (
		<Box data-testid="mc-multi-kpi-row" sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: "100%" }}>
			{cards
				.filter((card) => revealWage || !card.wageGated)
				.map((card) => (
					<CommonDashboardBox
						key={card.testId}
						data-testid={card.testId}
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "start",
							gap: 1,
							flex: 1,
							minWidth: KPI_CARD_MIN_WIDTH,
							height: STATISTICS_CARD_HEIGHT,
						}}
					>
						<CommonDashboardHeader leading={<InsightsIcon />} label={t(card.labelKey)} />
						<DashboardTypography mainText={card.value} />
					</CommonDashboardBox>
				))}
		</Box>
	);
};
